import { appApi, idTag } from '@/app/redux/appApi';
import { Organisation } from '@/common/types/organisation';
import { GameSettings } from '@/common/types/student';
import api from '@/db/apiClient';

export const organisationApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getOrganisation: build.query<Organisation | null, string>({
      query: async (orgId) =>
        (await api.asEducator.getOrganisation(orgId)) ?? null,
      onCacheEntryAdded: async (
        orgId,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) => {
        await cacheDataLoaded;
        const unsubscribe = await api.asEducator.subscribeToOrganisation(
          orgId,
          (updated) => {
            updateCachedData((draft) => {
              if (draft) Object.assign(draft, updated);
            });
          },
        );
        await cacheEntryRemoved;
        unsubscribe();
      },
      providesTags: (res) => (res ? [idTag('Organisations', res.id)] : []),
    }),

    getOrganisations: build.query<Organisation[], string[]>({
      query: (orgIds) => api.asEducator.getOrganisations(orgIds),
      providesTags: (res) =>
        res ? res.map((org) => idTag('Organisations', org.id)) : [],
    }),

    updateOrganisationDefaultSettings: build.mutation<
      null,
      { orgId: string; updates: Partial<GameSettings> }
    >({
      query: ({ orgId, updates }) =>
        api.asEducator.updateOrganisationDefaultSettings(orgId, updates),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),

    updateOrganisationName: build.mutation<
      null,
      { orgId: string; name: string }
    >({
      query: ({ orgId, name }) =>
        api.asEducator.updateOrganisationName(orgId, name),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),

    removeMemberFromOrganisation: build.mutation<
      null,
      { orgId: string; memberId: string }
    >({
      query: ({ orgId, memberId }) =>
        api.asEducator.removeMemberFromOrganisation(orgId, memberId),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),

    updateOrganisationMemberRole: build.mutation<
      null,
      { orgId: string; memberId: string; role: 'admin' | 'member' }
    >({
      query: ({ orgId, memberId, role }) =>
        api.asEducator.changeOrganisationMemberRole(orgId, memberId, role),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),

    markHasViewedResults: build.mutation<null, { orgId: string }>({
      query: async ({ orgId }) =>
        api.asEducator.markOrganisationHasViewedResults(orgId),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),

    markActivationChecklistDismissed: build.mutation<null, { orgId: string }>({
      query: async ({ orgId }) =>
        api.asEducator.markActivationChecklistDismissed(orgId),
      invalidatesTags: (_res, _err, { orgId }) => [
        idTag('Organisations', orgId),
      ],
    }),
  }),
});

export const {
  useGetOrganisationQuery,
  useGetOrganisationsQuery,
  useUpdateOrganisationDefaultSettingsMutation,
  useUpdateOrganisationNameMutation,
  useRemoveMemberFromOrganisationMutation,
  useUpdateOrganisationMemberRoleMutation,
  useMarkHasViewedResultsMutation,
  useMarkActivationChecklistDismissedMutation,
} = organisationApi;
